import { ApolloClient, from, HttpLink, InMemoryCache, NormalizedCacheObject } from '@apollo/client';
import { API_ENDPOINTS, ApiNameType, getApolloLink, IApolloClientOptions } from '@netfront/common-library';
import { createContext, useCallback, useContext, useMemo, useRef } from 'react';

type Cache = Partial<Record<ApiNameType, ApolloClient<NormalizedCacheObject>>>;

type GraphqlClientsContext = {
  getClient: (endpoint: ApiNameType) => ApolloClient<NormalizedCacheObject>;
};

const GqlClientContext = createContext<GraphqlClientsContext>({
  getClient: () => {
    throw new Error('Not Implemented');
  },
});

export const getApolloClient = ({
  apiName,
  authTokensCookieOptionalAttributes,
  customBuildGuid,
  isPublic,
  onUnauthenticated,
  product,
  projectId,
  requestCredentials,
}: IApolloClientOptions) => {
  const apiEndpoint = API_ENDPOINTS[apiName];

  if (!apiEndpoint) {
    throw new Error(`Api endpoint not found for ${apiName}`);
  }

  const httpLink = new HttpLink({
    uri: apiEndpoint,
  });

  const apolloLink = getApolloLink({
    authTokensCookieOptionalAttributes,
    customBuildGuid,
    isPublic,
    onUnauthenticated,
    product,
    projectId,
  });

  const link = from([apolloLink, httpLink]);

  return new ApolloClient({
    cache: new InMemoryCache({
      typePolicies: {
        AppMutations: {
          keyFields: [],
        },
        AssetMutations: {
          keyFields: [],
        },
        BookmarkMutations: {
          keyFields: [],
        },
        CaptionMutation: {
          keyFields: [],
        },
        CssStyleMutation: {
          keyFields: [],
        },
        CalendarMutation: {
          keyFields: [],
        },
        CssMutation: {
          keyFields: [],
        },
        ContentEventMutations: {
          keyFields: [],
        },
        ContentGroupMutations: {
          keyFields: [],
        },
        ContentPageMutations: {
          keyFields: [],
        },
        ContentSectionMutations: {
          keyFields: [],
        },
        ContentSnippetMutations: {
          keyFields: [],
        },
        CustomFieldMutation: {
          keyFields: [],
        },
        CustomFieldResponseMutation: {
          keyFields: [],
        },
        DirectoryUserMutation: {
          keyFields: [],
        },
        DirectoryLinkMutation: {
          keyFields: [],
        },
        DirectoryMutation: {
          keyFields: [],
        },
        DirectoryTypeMutation: {
          keyFields: [],
        },
        UserFlowGroupAccessMutation: {
          keyFields: [],
        },
        SectionGroupMutation: {
          keyFields: [],
        },
        SectionGroupConfigurationMutation: {
          keyFields: [],
        },
        ClusterMutation: {
          keyFields: [],
        },
        SummaryFeedbackMutation: {
          keyFields: [],
        },
        UnitModeMutation: {
          keyFields: [],
        },
        AppUserDataMutation: {
          keyFields: [],
        },
        ContentSnippetQuestionTriggerMutation: {
          keyFields: [],
        },
        RegionMutation: {
          keyFields: [],
        },
        FeedbackMutation: {
          keyFields: [],
        },
        FeedbackTypeMutation: {
          keyFields: [],
        },
        ProjectMutations: {
          keyFields: [],
        },
        QuestionAnswer: {
          keyFields: [],
        },
        QuestionConfiguration: {
          keyFields: [],
        },
        QuestionResponseSetMutation: {
          keyFields: [],
        },
        QuestionActionMutation: {
          keyFields: [],
        },
        CharacterMutation: {
          keyFields: [],
        },
        BubbleTextMutation: {
          keyFields: [],
        },
        ReportMutation: {
          keyFields: [],
        },
        SliderMutations: {
          keyFields: [],
        },
        TagMutation: {
          keyFields: [],
        },
        TextTooltipMutation: {
          keyFields: [],
        },
        UrlRedirectMutations: {
          keyFields: [],
        },
        UserFlowMutations: {
          keyFields: [],
        },
        UserFlowAccessModeMutation: {
          keyFields: [],
        },
        UserFlowStepMutations: {
          keyFields: [],
        },
        UserFlowStepTrackMutations: {
          keyFields: [],
        },
        UserMutations: {
          keyFields: [],
        },
        QuestionConfigurationMultiResponseHeaderMutations: {
          keyFields: [],
        },

        AuditLogQuery: {
          keyFields: [],
        },
        UserFlowGroupAccessQuery: {
          keyFields: [],
        },
        AppQueries: {
          keyFields: [],
        },
        AppUserDataQuery: {
          keyFields: [],
        },
        CssStyleQuery: {
          keyFields: [],
        },
        AssetQueries: {
          keyFields: [],
        },
        CustomFieldQuery: {
          keyFields: [],
        },
        ClusterQuery: {
          keyFields: [],
        },
        ContainerQuery: {
          keyFields: [],
        },
        ContentQuery: {
          keyFields: [],
        },
        UnitModeQuery: {
          keyFields: [],
        },
        BookmarkQuery: {
          keyFields: [],
        },
        CalendarQueries: {
          keyFields: [],
        },
        CharacterQuery: {
          keyFields: [],
        },
        ContentEventQueries: {
          keyFields: [],
        },
        ContentGroupQueries: {
          keyFields: [],
        },
        ContentPageQueries: {
          keyFields: [],
        },
        ContentSectionQueries: {
          keyFields: [],
        },
        ContentSnippetQueries: {
          keyFields: [],
        },
        DirectoryQueries: {
          keyFields: [],
        },
        DirectoryTypeQuery: {
          keyFields: [],
        },
        String: {
          keyFields: [],
        },
        FeedbackQuery: {
          keyFields: [],
        },
        FeedbackTypeQuery: {
          keyFields: [],
        },
        QuestionAnswerQuery: {
          keyFields: [],
        },
        QuestionConfigurationQuery: {
          keyFields: [],
        },
        QuestionResponseSetQuery: {
          keyFields: [],
        },
        ProjectQueries: {
          keyFields: [],
        },
        ReportQuery: {
          keyFields: [],
        },
        SliderQueries: {
          keyFields: [],
        },
        SystemQueries: {
          keyFields: [],
        },
        TagQuery: {
          keyFields: [],
        },
        TextTooltipQuery: {
          keyFields: [],
        },
        UrlRedirectQueries: {
          keyFields: [],
        },
        QuestionActionQuery: {
          keyFields: [],
        },
        UserFlowQueries: {
          keyFields: [],
        },
        UserFlowAccessModeQuery: {
          keyFields: [],
        },
        UserFlowStepQuery: {
          keyFields: [],
        },
        UserFlowStepTrackQuery: {
          keyFields: [],
        },
        UserQueries: {
          keyFields: [],
        },
        SectionGroupQuery: {
          keyFields: [],
        },
        SectionGroupConfigurationQuery: {
          keyFields: [],
        },
      },
    }),
    credentials: requestCredentials,
    link,
  });
};

export default function ApolloClientsProvider(props: React.PropsWithChildren<{ initialCache?: Cache }>) {
  const clients = useRef<Partial<Record<ApiNameType, ApolloClient<NormalizedCacheObject>>>>(props.initialCache ?? {});

  const getClient = useCallback((endpoint: ApiNameType) => {
    if (!clients.current[endpoint]) {
      const client = getApolloClient({
        apiName: endpoint,
        projectId: process.env.NEXT_PUBLIC_EKARDO_PROJECT_ID!,
      });

      clients.current[endpoint] = client;
    }
    return clients.current[endpoint]!;
  }, []);

  const value = useMemo(() => ({ getClient }), [getClient]);

  return <GqlClientContext.Provider value={value}>{props.children}</GqlClientContext.Provider>;
}

export function useGeladaClient() {
  return useContext(GqlClientContext).getClient('gelada');
}

export function useEkardoClient() {
  return useContext(GqlClientContext).getClient('ekardo');
}

export function useHowlerClient() {
  return useContext(GqlClientContext).getClient('howler');
}

let publicEkardoClient: ApolloClient<NormalizedCacheObject> | undefined;

export function usePublicEkardoClient() {
  if (!publicEkardoClient) {
    publicEkardoClient = getApolloClient({
      apiName: 'ekardo',
      isPublic: true,
      projectId: process.env.NEXT_PUBLIC_EKARDO_PROJECT_ID!,
    });
  }
  return publicEkardoClient;
}
